import React from 'react'

import {
  AlignItems,
  Box,
  Display,
  FlexDirection,
  JustifyContent,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { GradientInfoHeaderContentBlock } from '@library/api-menu-experience'

type GradientInfoHeaderProps = {
  contentBlock: GradientInfoHeaderContentBlock
}

const GradientInfoHeaderContainer = withCitrusTheme(Box, () => ({
  width: '100%',
  maxWidth: '1280px',
  height: '7rem',
  margin: '3rem auto -2rem',
  position: 'relative',
  paddingLeft: 'var(--spacing-XXL)',
  paddingRight: 'var(--spacing-XXL)',
}))

const GradientMainImage = withCitrusTheme('img', () => ({
  width: '60%',
  objectFit: 'cover',
  '@media (min-width: 544px)': {
    width: '40%',
  },
}))

const GradientInfoText = withCitrusTheme(Box, () => ({
  fontFamily: 'var(--fontFamily-AxiformaBook), sans-serif',
  fontSize: 'var(--fontSize-XS)',
  '@media (min-width: 544px)': {
    fontSize: 'var(--fontSize-MD)',
  },
}))

const GradientDescription = withCitrusTheme('p', () => ({
  paddingTop: '0.5rem',
  margin: 0,
}))

const getGradient = (
  imageLocation: 'left' | 'right',
  isDesktop: boolean,
  gradientColor: string,
) => {
  if (imageLocation === 'left') {
    return isDesktop
      ? `linear-gradient(90deg, rgba(0, 83, 199, 0) 20%, ${gradientColor} 30%)`
      : `linear-gradient(90deg, rgba(0, 83, 199, 0) 26.58%, ${gradientColor} 55%)`
  }

  return isDesktop
    ? `linear-gradient(270deg, rgba(0, 83, 199, 0) 20%, ${gradientColor} 30%)`
    : `linear-gradient(270deg, rgba(0, 83, 199, 0) 26.58%, ${gradientColor} 55%)`
}

export const GradientInfoHeader = ({ contentBlock }: GradientInfoHeaderProps) => {
  const titleImage = contentBlock.headerImage[0]
  const mainImage = contentBlock.image[0]
  const isImageLeft = contentBlock.imageLocation === 'left'

  return (
    <GradientInfoHeaderContainer data-testid="gradient-info-header">
      <Box
        data-testid="gradient-image-background"
        display={Display.Flex}
        justifyContent={isImageLeft ? JustifyContent.FlexStart : JustifyContent.FlexEnd}
        height="100%"
        style={{ position: 'relative' }}
      >
        {mainImage ? (
          <GradientMainImage
            src={mainImage.url}
            alt={mainImage.altText || contentBlock.title}
            data-testid="gradient-info-header-main-image"
          />
        ) : null}
        <Box
          data-testid="gradient-background-mobile"
          display={['block', 'none', 'none']}
          width="100%"
          height="100%"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            backgroundImage: getGradient(
              contentBlock.imageLocation,
              false,
              contentBlock.gradientColor,
            ),
          }}
        />
        <Box
          data-testid="gradient-background-desktop"
          display={['none', 'block', 'block']}
          width="100%"
          height="100%"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            backgroundImage: getGradient(
              contentBlock.imageLocation,
              true,
              contentBlock.gradientColor,
            ),
          }}
        />
        <GradientInfoText
          data-testid="gradient-info-text"
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          justifyContent={JustifyContent.Center}
          alignItems={AlignItems.FlexStart}
          width="60%"
          height="100%"
          style={{
            position: 'absolute',
            left: isImageLeft ? undefined : 0,
            right: isImageLeft ? 0 : undefined,
            color: contentBlock.color,
            paddingLeft: isImageLeft ? 0 : '1rem',
            paddingRight: isImageLeft ? '1rem' : 0,
            textAlign: isImageLeft ? 'right' : 'left',
          }}
        >
          {titleImage ? (
            <img
              data-testid="gradient-info-header-image"
              src={titleImage.url}
              alt={titleImage.altText || contentBlock.title}
              style={{
                width: 200,
                height: 'auto',
                alignSelf: isImageLeft ? 'flex-end' : 'flex-start',
              }}
            />
          ) : null}
          <GradientDescription
            data-testid="gradient-description"
            style={{
              color: contentBlock.color,
              alignSelf: isImageLeft ? 'flex-end' : 'flex-start',
            }}
          >
            {contentBlock.description}
          </GradientDescription>
        </GradientInfoText>
      </Box>
    </GradientInfoHeaderContainer>
  )
}
